import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ReinviteSpaceModal from '../../../components/shared/match-me-if-you-can/reinvite-space-modal'
import Button from '../../../components/tailwind/Button'
import { QUERY_KEYS } from '../../../constants/constants'
import { useAivyContext, useLanguageContext } from '../../../context'
import { useAnswersBySpaceQuery } from '../../../hooks/useAnswersBySpaceQuery'

export default function IncompleteDatasetWarning({ career, talent }) {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { system } = useAivyContext()
  const queryClient = useQueryClient()

  const [showReinviteSpaceModal, setShowReinviteSpaceModal] = useState(false)

  const { data: answers, isLoading } = useAnswersBySpaceQuery({
    spaceId: talent?.id
  })

  const talentName = talent?.person
    ? `${talent.person.firstname || talent.person.invitation_firstname} ${talent.person.lastname || talent.person.invitation_lastname}`
    : t('common.unknown_talent')

  const careerName = career?.title || career?.name || t('common.unknown_career')

  const missingChallengeNames = useMemo(() => {
    if (!career?.app_settings?.challenges || !answers) return []

    try {
      const requiredChallenges = JSON.parse(career.app_settings.challenges).map(
        (c) => c.exam_id
      )

      const completedChallenges = answers.map((a) => a.exam_id)

      const missingIds = requiredChallenges.filter(
        (id) => !completedChallenges.includes(id)
      )

      const systemChallenges = system?.challenges
        ? JSON.parse(system.challenges.data)
        : null

      if (systemChallenges) {
        return missingIds.map((examId) => {
          const challenge = systemChallenges[examId]
          if (challenge?.title?.[language]) {
            return challenge.title[language]
          }
          return examId
        })
      }

      return missingIds
    } catch {
      return []
    }
  }, [career, answers, system, language])

  const alreadyReinvited =
    talent?.all_challenges_unlocked && missingChallengeNames.length > 0
  const showReinviteOption =
    !talent?.all_challenges_unlocked && missingChallengeNames.length > 0

  // Don't render during loading to prevent flicker
  if (isLoading || !career || !talent) {
    return null
  }

  const updateHandler = () => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.spaceById(talent.id)
    })
  }

  return (
    <>
      <div className='mx-6 my-8 rounded-md bg-yellow-50 p-4'>
        <div className='flex'>
          <div className='shrink-0'>
            <ExclamationTriangleIcon
              className='size-5 text-yellow-400'
              aria-hidden='true'
            />
          </div>
          <div className='ml-3 flex-1'>
            <h3 className='text-sm font-medium text-yellow-800'>
              {t('discover.talent_match_section.incomplete_dataset.title')}
            </h3>
            <div className='mt-2 text-sm text-yellow-700'>
              <p>
                {t(
                  'discover.talent_match_section.incomplete_dataset.description',
                  {
                    talentName,
                    careerName
                  }
                )}
              </p>
            </div>

            {missingChallengeNames.length > 0 && (
              <div className='mt-4'>
                <p className='text-sm font-medium text-yellow-800'>
                  {t(
                    'discover.talent_match_section.incomplete_dataset.missing_label'
                  )}
                  :
                </p>
                <div className='mt-2 flex flex-wrap gap-2'>
                  {missingChallengeNames.map((name) => (
                    <span
                      key={name}
                      className='inline-flex items-center rounded-full border border-yellow-400 bg-white px-3 py-1 text-xs font-medium text-yellow-900 shadow-sm'
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {showReinviteOption && (
              <div className='mt-4'>
                <p className='mb-3 text-sm text-yellow-700'>
                  {t(
                    'discover.talent_match_section.incomplete_dataset.solution_hint',
                    { talentName }
                  )}
                </p>

                <Button.SecondaryBase
                  onClick={() => setShowReinviteSpaceModal(true)}
                  text={t(
                    'discover.talent_match_section.incomplete_dataset.action_button'
                  )}
                />

                {/* Login hint */}
                <div className='mt-3 rounded-md bg-blue-50 p-3'>
                  <p className='text-xs text-blue-800'>
                    <span className='font-medium'>
                      {t(
                        'discover.talent_match_section.incomplete_dataset.login_hint.title'
                      )}
                    </span>{' '}
                    {t(
                      'discover.talent_match_section.incomplete_dataset.login_hint.description'
                    )}
                  </p>
                </div>
              </div>
            )}
            {alreadyReinvited && (
              <div className='mt-4 rounded-md bg-yellow-100 p-3'>
                <p className='text-sm font-medium text-yellow-800'>
                  ✓{' '}
                  {t(
                    'discover.talent_match_section.incomplete_dataset.already_requested'
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ReinviteSpaceModal
        space={talent}
        updateHandler={updateHandler}
        open={showReinviteSpaceModal}
        setOpen={() => setShowReinviteSpaceModal(false)}
      />
    </>
  )
}

IncompleteDatasetWarning.propTypes = {
  career: PropTypes.object,
  talent: PropTypes.object
}
